import cn from 'classnames';

import ArrowLink from 'components/BaseHelpers/ArrowLink';
import Image from 'components/BaseHelpers/Image';
import PatternBackground from 'components/BaseHelpers/PatternBackground';
import RichText from 'components/BaseHelpers/RichText';
import Text from 'components/BaseHelpers/Text';

import { ComponentProps } from '../../../types/component-props';
import { CoreHomeHeroData } from '../../../types/components';

import styles from './CoreHomeHero.module.scss';

export type CoreHomeHeroProps = ComponentProps & CoreHomeHeroData;

const CoreHomeHero = (props: CoreHomeHeroProps): JSX.Element => {
  const { header, description, primaryNavigationLinks, heroImage } = props.fields;

  return (
    <div
      className={cn(styles.main, {
        [styles['with-hero-image']]: Boolean(heroImage),
      })}
      data-component="CoreHomeHero"
    >
      {heroImage ? (
        <Image className={styles['background-image']} {...heroImage} fill fetchPriority="high" />
      ) : (
        <PatternBackground
          className={styles['background-image']}
          fetchPriority="high"
          size="medium"
          theme="dark"
        />
      )}

      <div className={cn(styles.container, 'container-10')}>
        <div className={styles.content}>
          <Text tag="h2" className={cn(styles.header, 'heading-h1')} field={header} />
          <RichText className={cn(styles.description, 'body-large')} field={description} />
        </div>
        <ul className={styles.links}>
          {primaryNavigationLinks?.map((item, index) => (
            <li key={index} className={styles['link-item']}>
              <ArrowLink
                field={item.link.label}
                href={item.link.href}
                className={styles['link']}
                variant="medium"
                theme="on-dark"
                openNewTab={item.link.target === '_blank'}
              />
              {item.hoverMedia?.type === 'image' ? (
                <Image
                  key={index}
                  className={styles['hover-image']}
                  src={item.hoverMedia.url}
                  alt={item.hoverMedia.alt}
                  fill
                />
              ) : item.hoverMedia?.type === 'video' ? (
                <video
                  key={index}
                  className={styles['hover-video']}
                  src={item.hoverMedia.url}
                  aria-label={item.hoverMedia.alt}
                  loop
                  muted
                  playsInline
                  // eslint-disable-next-line react/no-unknown-property
                  onTransitionStart={(event) => {
                    if (event.nativeEvent.propertyName === 'opacity') {
                      const video = event.target as HTMLVideoElement;
                      if (video.currentTime === 0 || video.paused) {
                        video.play();
                      } else {
                        video.pause();
                      }
                    }
                  }}
                />
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default CoreHomeHero;
